import React from "react";
import { any, object, func } from "prop-types";
import { connect } from "react-redux";
import Resources from "../../components/Resources";
import {
  downloadResourcePdf,
  resourcePdfUrl,
  submitResourceAgreement,
} from "../../api/profileApi";

import {
  foodHandlingSubmitted,
  manualHandlingSubmitted,
  cashAlcoholSubmitted,
} from "../../redux/actions/formActions";

import { isDownloadAttrSupported, forceDownload } from "./util";

import ConfirmPDFResource from "../../components/ConfirmPDFResource/ConfirmPDFResource";
import FormPDFResource from "../../components/FormPDFResource/FormPDFResource";

const resourcePdfDownload = async (formName, filename, complete = false) => {
  if (isDownloadAttrSupported()) {
    return downloadResourcePdf(formName, complete).then((response) =>
      forceDownload(response, filename)
    );
  }
  return window.open(resourcePdfUrl(formName, complete), "_blank");
};

const submitResource = async (formName, name, filename) => {
  return submitResourceAgreement(name, formName).then((response) => {
    if (isDownloadAttrSupported()) {
      return forceDownload(response, filename);
    }
    return window.open(resourcePdfUrl(formName, true), "_blank");
  });
};

const foodHandlersForm = (appData, setFoodHandlingSubmitted) => {
  return (
    <ConfirmPDFResource
      hasFile={appData.foodHandling}
      title="Food handlers agreement"
      description="Please download this form and read it fully, you do not have to complete the fields. Once you have ticked the box to confirm you have read and understood the form and agree to the declaration, we will complete the fields on the form e.g. name, signature, date, for you. If you have any queries about the form, please speak to your HRGO consultant."
      agreementText="I understand as a food handler that it is a legal requirement for me to report any of the conditions stated in this document and will comply with all the stated requirements."
      downloadPDFFile={async (complete) =>
        resourcePdfDownload(
          "foodform",
          "Food handlers agreement to report infectious diseases.pdf",
          complete
        )
      }
      submitResourceAgreement={async () => {
        setFoodHandlingSubmitted();
        return submitResource(
          "foodAgreement",
          `${appData.firstName} ${appData.lastName}`,
          "Food handlers agreement to report infectious diseases.pdf"
        );
      }}
    />
  );
};

const ManualHandlingForm = (appData, setManualHandlingSubmitted) => {
  return (
    <ConfirmPDFResource
      hasFile={appData.manualHandling}
      title="Manual handling - lifting safely"
      description="Please download this form and read it fully, you do not have to complete the fields. Once you have ticked the box to confirm you have read and understood the form and agree to the declaration, we will complete the fields on the form e.g. name, signature, date, for you. If you have any queries about the form, please speak to your HRGO consultant."
      agreementText="I have read and understood the Lifting Safely advice and will adhere to the summary stated in this document."
      downloadPDFFile={async (complete) =>
        resourcePdfDownload(
          "getManualHandlingPDF",
          "Manual handling - lifting safely.pdf",
          complete
        )
      }
      submitResourceAgreement={async () => {
        setManualHandlingSubmitted();
        return submitResource(
          "manualAgreement",
          `${appData.firstName} ${appData.lastName}`,
          "Manual Handling - Lifting Safely.pdf"
        );
      }}
    />
  );
};

const CashAlcoholForm = (appData, setCashAlcoholSubmitted) => {
  return (
    <ConfirmPDFResource
      hasFile={appData.cashAlcohol}
      title="Cash and alcohol responsibilities"
      description="Please download this form and read it fully, you do not have to complete the fields. Once you have ticked the box to confirm you have read and understood the form and agree to the declaration, we will complete the fields on the form e.g. name, signature, date, for you. If you have any queries about the form, please speak to your HRGO consultant."
      agreementText="I understand that failure to fulfill any of the responsibilities stated in this document may lead to the assignment being terminated immediately."
      downloadPDFFile={async (complete) =>
        resourcePdfDownload(
          "getCashAlcoholPDF",
          "Cash & alcohol responsibilities.PDF",
          complete
        )
      }
      submitResourceAgreement={async () => {
        setCashAlcoholSubmitted();
        return submitResource(
          "cashAlcoholAgreement",
          `${appData.firstName} ${appData.lastName}`,
          "Cash & alcohol responsibilities.pdf"
        );
      }}
    />
  );
};

const NightWorkerForm = (appData) => {
  return (
    <FormPDFResource
      route="night-worker-health-assessment"
      hasFile={appData.nightWorkerHealth}
      title="Night worker health assessments"
      description="You are required to complete and submit this form before you start work. Please ensure all fields that are marked as mandatory are completed to the best of your knowledge. If you have any queries about the form, please speak to your line manager."
      downloadPDFFile={(complete) =>
        downloadResourcePdf("getNightWorkerHealthAssessmentPDF", complete).then(
          (response) =>
            forceDownload(response, "Night_Worker_Health_Assessment.pdf")
        )
      }
    />
  );
};

const AuthorityForPayment = (appData) => {
  return (
    <FormPDFResource
      route="authority-for-payments-assessment"
      hasFile={appData.authorityForPayments}
      title="Authority for Payment of Wages into a 3rd Party Bank Account"
      description="You are required to complete and submit this form before you start work. Please ensure all fields that are marked as mandatory are completed to the best of your knowledge. If you have any queries about the form, please speak to your line manager."
      downloadPDFFile={(complete) =>
        downloadResourcePdf(
          "getAuthorityForPaymentsAssessmentPDF",
          complete
        ).then((response) =>
          forceDownload(response, "Authoring for payment into 3rd party.pdf")
        )
      }
    />
  );
};

const ResourcesHRGOContainer = ({
  appData,
  setFoodHandlingSubmitted,
  setManualHandlingSubmitted,
  setCashAlcoholSubmitted,
}) => {
  const res = [];
  res.push(foodHandlersForm(appData, setFoodHandlingSubmitted));
  res.push(ManualHandlingForm(appData, setManualHandlingSubmitted));
  res.push(CashAlcoholForm(appData, setCashAlcoholSubmitted));
  res.push(NightWorkerForm(appData));
  res.push(AuthorityForPayment(appData));

  return <Resources resources={res} title="Assignment Forms" />;
};

ResourcesHRGOContainer.propTypes = {
  appData: object.isRequired,
  setFoodHandlingSubmitted: func,
  setManualHandlingSubmitted: func,
  setCashAlcoholSubmitted: func,
};

const mapStateToProps = (state) => ({
  appData: state.core.app,
});

const mapDispatchToProps = {
  setFoodHandlingSubmitted: foodHandlingSubmitted,
  setManualHandlingSubmitted: manualHandlingSubmitted,
  setCashAlcoholSubmitted: cashAlcoholSubmitted,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(ResourcesHRGOContainer);
